import { formatDate } from "@taabl/common-lib/calculators/date";
import { TransferType } from "@taabl/common-lib/constants/TransferType";
import { Form, Formik } from "formik";
import React, { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import * as yup from "yup";
import FormikDropdown from "../../components/Forms/FormikDropdown";
import FormikTextField from "../../components/Forms/FormikTextField";
import FormikTypeAhead from "../../components/Forms/FormikTypeAhead";
import LoaderButton from "../../components/LoaderButton";
import LoggedInLayout from "../../layouts/loggedinlayout";
import { Constants } from "../../libs/Constants";
import { create, get, getList } from "../../libs/dbFunctions";
import { EmployeeInfo } from "./EmployeeInfo";
const initialValues = {
  employee: "",
  purchasePeriod: "",
  amount: 0,
  account: "",
  send: "send",
  transactionType: TransferType.STANDARD.value,
};
const schema = yup.object({
  employee: yup.string().required("You are required to select a employee"),
  send: yup.string().required("Select transaction type"),
  purchasePeriod: yup
    .string()
    .required("You are required to select Purchase Period"),
  amount: yup
    .number()
    .transform(function (value, originalValue) {
      if (this.isType(value) && value !== null) {
        return value;
      }
      return originalValue ? parseFloat(originalValue.replace(/,/g, '')) : 0;
    })
    .required("Amount is required")
    .min(0, "Amount should be greater than 0"),
  account: yup.string().required("You are required to select an account"),
  transactionType: yup.string().required("You are required to select a type"),
});

const Transactions = ({ activeCompany }) => {
  const [acccounts, setAccounts] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [allPeriods, setAllPeriods] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(values) {
    const selectedPeriod = allPeriods.find(
      (a) => a.periodStartdate === Number(values.purchasePeriod)
    );
    // Call API to do the transaction
    try {
      await create(Constants.dbTypes.TRANSACTION, {
        amount: parseFloat(values.amount.replace(/,/g, '')),
        account: values.account,
        periodStartdate: selectedPeriod.periodStartdate,
        periodEnddate: selectedPeriod.periodEnddate,
        userId: user.id,
        companyId: user.companyId,
        send: values.send === "send",
        transactionType: values.transactionType,
        paymentMode: 'dwolla'
      });
      toast.success(
        "Transfer initiated successfully. It might take 1-2 days to settle the transfer"
      );
    } catch (e) {
      if (e.response && e.response.data && e.response.data.error) {
        toast.error(e.response.data.error);
      } else {
        toast.error(e);
      }
    }
  }
  //this function is used to get user accounts
  async function getAccounts(userId) {
    setLoading(true);
    let usr = await get(Constants.dbTypes.USER, userId);
    setLoading(false);
    if (usr) {
      setUser(usr);
      // Get the purchase period data for the company the user is enrolled to
      const periodDetails = await getList(Constants.dbTypes.PERIOD, {
        id: usr.companyId,
      });
      if (periodDetails && periodDetails.length > 0) {
        setAllPeriods(periodDetails);
        setPeriods(
          periodDetails.map((period) => {
            return {
              value: period.periodStartdate,
              name: ` Period - ${formatDate(
                period.periodStartdate,
                Constants.DATEFORMAT.MONYYYY
              )}`,
            };
          })
        );
      }
      if (usr.accounts) {
        setAccounts(
          usr.accounts.map((acc) => {
            return { value: acc.id, name: `${acc.name} - ${acc.bankName}` };
          })
        );
      }
    }
  }
  return (
    <LoggedInLayout title="Transfer">
      <Row>
        <Col sm={4}>
          <Formik
            validationSchema={schema}
            onSubmit={onSubmit}
            initialValues={initialValues}
            validateOnMount
          >
            {({ isValid, isSubmitting, values, setFieldValue }) => (
              <Form className="w-100">
                <FormikTypeAhead
                  size="lg"
                  name="employee"
                  dbType={Constants.dbTypes.USER}
                  company={{
                    id: activeCompany.id,
                  }}
                  labelId={"id"}
                  labelKey={"name"}
                  constructLabel={(record) => {
                    return `${record.firstName} ${record.lastName} (${record.email})`;
                  }}
                  label="Employee"
                  placeholder="Search by Full name..."
                  className="mb-4"
                  onChange={(value) => {
                    getAccounts(value);
                    setFieldValue("account", "", true);
                  }}
                ></FormikTypeAhead>

                {values.employee && (
                  <div>
                    <FormikTextField
                      size="lg"
                      controlId="amount"
                      label="Amount"
                      type="text"
                      name="amount"
                      step={"0.01"}
                      className="mb-4"
                    />

                    <FormikDropdown
                      name="purchasePeriod"
                      label="Select Purchase Period"
                      size={"lg"}
                      options={periods}
                      placeholder="Select Purchase Period"
                    />

                    <FormikDropdown
                      size={"lg"}
                      name="account"
                      label="Select Account"
                      options={acccounts}
                      placeholder="Select Account"
                    />

                    <FormikDropdown
                      name="send"
                      size={"lg"}
                      label="Select Transaction"
                      options={[
                        { name: "Send", value: "send" },
                        { name: "Receive", value: "receive" },
                      ]}
                      placeholder="Select Transaction"
                    />

                    <FormikDropdown
                      name="transactionType"
                      size={"lg"}
                      label="Select Transaction Type"
                      options={[
                        {
                          name: TransferType.STANDARD.label,
                          value: TransferType.STANDARD.value,
                        },
                        {
                          name: TransferType.SAME_DAY.label,
                          value: TransferType.SAME_DAY.value,
                        },
                      ]}
                      placeholder="Select Transaction"
                    />

                    <LoaderButton
                      block
                      type="submit"
                      variant="primary"
                      size="lg"
                      text="Next"
                      className="mb-2"
                      isLoading={isSubmitting}
                      disabled={!isValid || isSubmitting}
                    >
                      Commit
                    </LoaderButton>
                  </div>
                )}
              </Form>
            )}
          </Formik>
        </Col>
        {!user && loading && <>Loading user information...</>}
        {user && (
          <Col sm={4}>
            <EmployeeInfo
              user={user}
              activeCompany={activeCompany}
              periods={allPeriods}
            />
          </Col>
        )}
      </Row>
    </LoggedInLayout>
  );
};

export default Transactions;
